import { ref } from 'vue';

export interface UseSwipeToDismissOptions {
    /** Called once an item has been swiped away and its settle animation finished. */
    onDismiss: (id: number) => void;
    /** Upward distance in pixels after which releasing dismisses the item. */
    threshold?: number;
    /** Upward speed in px/ms that dismisses regardless of distance (a flick). */
    velocity?: number;
    /** Duration of the settle animation in ms (both dismissing and springing back). */
    settleDuration?: number;
}

export interface UseSwipeToDismissReturn {
    onTouchStart: (id: number, event: TouchEvent) => void;
    onTouchMove: (event: TouchEvent) => void;
    onTouchEnd: () => void;
    styleFor: (id: number) => Record<string, string>;
}

/** Downward movement is damped: the item follows the finger but stays in place. */
const DOWNWARD_DAMPING = 0.2;

/** Distance over which the item fades out completely while being dragged up. */
const FADE_DISTANCE = 120;

/**
 * Swipe-up-to-dismiss for stacked overlay items (toasts). Touch only, so mouse
 * and keyboard interaction on desktop is left untouched — the close button stays
 * the way out there.
 *
 * Only one item can be dragged at a time, which matches how a single finger
 * behaves, so the drag state is kept flat instead of per id.
 */
export function useSwipeToDismiss(options: UseSwipeToDismissOptions): UseSwipeToDismissReturn {
    const { onDismiss, threshold = 48, velocity = 0.35, settleDuration = 180 } = options;

    const activeId = ref<number | null>(null);
    const offset = ref(0);
    const isSettling = ref(false);

    let element: HTMLElement | null = null;
    let startY = 0;
    let startTime = 0;
    let lastY = 0;
    let lastTime = 0;

    const touchY = (event: TouchEvent): number | null =>
        event.touches?.[0]?.clientY ?? event.changedTouches?.[0]?.clientY ?? null;

    const reset = () => {
        activeId.value = null;
        offset.value = 0;
        isSettling.value = false;
        element = null;
    };

    const onTouchStart = (id: number, event: TouchEvent) => {
        const y = touchY(event);
        if (y === null) return;

        activeId.value = id;
        offset.value = 0;
        isSettling.value = false;
        element = (event.currentTarget as HTMLElement) ?? null;
        startY = lastY = y;
        startTime = lastTime = Date.now();
    };

    const onTouchMove = (event: TouchEvent) => {
        if (activeId.value === null || isSettling.value) return;

        const y = touchY(event);
        if (y === null) return;

        const delta = y - startY;
        offset.value = delta < 0 ? delta : delta * DOWNWARD_DAMPING;
        lastY = y;
        lastTime = Date.now();
    };

    const onTouchEnd = () => {
        if (activeId.value === null || isSettling.value) return;

        const id = activeId.value;
        // Positive speed means the finger moved upwards.
        const speed = (startY - lastY) / Math.max(lastTime - startTime, 1);
        const dismissed = offset.value <= -threshold || (offset.value < 0 && speed >= velocity);

        isSettling.value = true;
        // Send it past the top edge on dismiss, back into its slot otherwise.
        offset.value = dismissed ? -((element?.offsetHeight || 80) + 24) : 0;

        setTimeout(() => {
            // A late store timeout may already have taken this item; remove() is
            // idempotent, so calling it again is harmless.
            if (dismissed) onDismiss(id);
            reset();
        }, settleDuration);
    };

    const styleFor = (id: number): Record<string, string> => {
        if (activeId.value !== id) return {};

        const travelled = Math.abs(Math.min(offset.value, 0));

        return {
            transform: `translateY(${offset.value}px)`,
            opacity: String(Math.max(0, 1 - travelled / FADE_DISTANCE)),
            transition: isSettling.value
                ? `transform ${settleDuration}ms ease-out, opacity ${settleDuration}ms ease-out`
                : 'none',
        };
    };

    return { onTouchStart, onTouchMove, onTouchEnd, styleFor };
}
